import type { Board, BoardRegion } from '../protocol/ui';
import type { ObjectView, TableModel } from './table';

/** A board point that lies within this many board units of a region corner is that corner. */
const CORNER_TOLERANCE = 1.5;

const near = (a: [number, number], x: number, y: number) => Math.abs(a[0] - x) <= CORNER_TOLERANCE && Math.abs(a[1] - y) <= CORNER_TOLERANCE;

/**
 * Regions that touch a board node (the node is a corner of the region) or a board edge (both its end nodes are corners of the region).
 * Pure geometry on the Describe board; no game rules are consulted.
 */
export function adjacentRegions(board: Board, ref: string): BoardRegion[] {
  const node = board.nodes.find(n => n.id === ref);
  if (node) return board.regions.filter(r => r.points.some(p => near(p, node.x, node.y)));
  const edge = board.edges.find(e => e.id === ref);
  const a = edge && board.nodes.find(n => n.id === edge.a), b = edge && board.nodes.find(n => n.id === edge.b);
  if (!a || !b) return [];
  return board.regions.filter(r => r.points.some(p => near(p, a.x, a.y)) && r.points.some(p => near(p, b.x, b.y)));
}

/** The region object's own label without the trailing "(region id)" the host adds ("Grain, roll 4 (hex-11-8)" reads "Grain, roll 4"). */
function regionText(object: ObjectView, regionId: string): string {
  const suffix = ` (${regionId})`;
  return object.label.endsWith(suffix) ? object.label.slice(0, -suffix.length) : object.label;
}

/**
 * A describer for one table: "Beside Grain, roll 4 · Sea" for a node or edge object, from the regions that touch it and the observed
 * objects standing for those regions. It returns null for anything that is not a board node or edge, or whose board is unknown.
 * Display only; the stand-in index is built once, on first use.
 */
export function adjacencyDescriber(table: TableModel): (objectId: string) => string | null {
  let standIns: Map<string, ObjectView> | null = null;
  return objectId => {
    const object = table.objects.get(objectId);
    if (!object || !object.ref || object.kind !== 'node' && object.kind !== 'edge') return null;
    const board = table.boards.find(b => object.kind === 'node' ? b.nodes.some(n => n.id === object.ref) : b.edges.some(e => e.id === object.ref));
    if (!board) return null;
    if (!standIns) {
      standIns = new Map();
      for (const o of table.objects.values()) if (o.ref && (o.kind === 'tile' || o.kind === 'space') && !standIns.has(o.ref)) standIns.set(o.ref, o);
    }
    const parts = adjacentRegions(board, object.ref).map(r => {
      const stand = standIns!.get(r.id);
      return stand ? regionText(stand, r.id) : r.label;
    });
    return parts.length ? `Beside ${parts.join(' · ')}` : 'Beside no tile';
  };
}
